import { Check, Minus } from "lucide-react";

const TIERS = [
  { key: "free", name: "Free", price: "$0" },
  { key: "premium", name: "Full Guide", price: "$19", highlight: true },
  { key: "service", name: "Done-for-you", price: "$199 + state fee" },
];

// value: true = included, false = not included, string = note
const ROWS = [
  { label: "Step 0–2: prerequisites, name, registered agent", free: true, premium: true, service: true },
  { label: "Steps 3–9: filing, operating agreement, EIN, bank, franchise tax, licenses, compliance", free: false, premium: true, service: true },
  { label: "Interactive checklists & saved progress", free: true, premium: true, service: true },
  { label: "Startup cost estimator", free: "Preview", premium: true, service: true },
  { label: "AI assistant (answers from the guide)", free: "Steps 0–2 only", premium: true, service: true },
  { label: "Official links, fees & pro tips for every step", free: "Steps 0–2", premium: true, service: true },
  { label: "Printable checklist + PDF progress report", free: true, premium: true, service: true },
  { label: "Completion certificate (LinkedIn-ready)", free: false, premium: true, service: true },
  { label: "We file your Certificate of Formation for you", free: false, premium: false, service: true },
  { label: "Operating agreement drafted for you", free: false, premium: "Add-on $50", service: true },
  { label: "1 year registered agent service", free: false, premium: "Add-on $75", service: true },
  { label: "EIN obtained for you", free: false, premium: "Free DIY walkthrough", service: true },
  { label: "Personal email support (24h)", free: true, premium: true, service: "Priority" },
  { label: "30-day money-back guarantee", free: "—", premium: true, service: false },
  { label: "Lifetime access, no subscription", free: true, premium: true, service: true },
];

function Cell({ value, highlight }) {
  if (value === true) return <Check className={`mx-auto h-5 w-5 ${highlight ? "text-[#1E3A8A]" : "text-emerald-600"}`} aria-label="Included" />;
  if (value === false) return <Minus className="mx-auto h-4 w-4 text-[var(--c-muted)]/50" aria-label="Not included" />;
  return <span className="text-xs font-semibold text-[var(--c-muted)]">{value}</span>;
}

export default function PackageComparisonTable({ onPick }) {
  return (
    <section data-testid="package-comparison" className="px-4 pb-16 sm:pb-20">
      <div className="mx-auto max-w-5xl">
        <details className="group rounded-3xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] shadow-[6px_6px_0px_var(--c-shadow)]">
          <summary data-testid="package-comparison-toggle" className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-5">
            <div>
              <p className="font-heading text-xl font-black text-[var(--c-text)] sm:text-2xl">Compare packages, feature by feature</p>
              <p className="text-sm text-[var(--c-muted)]">Everything that&apos;s in each option — no surprises.</p>
            </div>
            <span className="rounded-full border-2 border-[var(--c-border)] bg-[var(--c-bg)] px-4 py-2 text-xs font-bold text-[var(--c-text)] shadow-[2px_2px_0px_var(--c-shadow)] group-open:hidden">Show table</span>
            <span className="hidden rounded-full border-2 border-[var(--c-border)] bg-[var(--c-bg)] px-4 py-2 text-xs font-bold text-[var(--c-text)] shadow-[2px_2px_0px_var(--c-shadow)] group-open:inline">Hide table</span>
          </summary>
          <div className="overflow-x-auto border-t-2 border-[var(--c-border)]">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead>
                <tr className="text-[var(--c-text)]">
                  <th className="px-6 py-4 text-xs font-bold uppercase tracking-wide text-[var(--c-muted)]">Feature</th>
                  {TIERS.map((t) => (
                    <th key={t.key} className={`px-4 py-4 text-center ${t.highlight ? "bg-[#1E3A8A]/10" : ""}`}>
                      <p className="font-heading text-base font-black">{t.name}</p>
                      <p className="text-xs font-semibold text-[var(--c-muted)]">{t.price}</p>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map((r) => (
                  <tr key={r.label} data-testid="comparison-row" className="border-t border-[var(--c-border-light)]">
                    <td className="px-6 py-3 text-[var(--c-text)]">{r.label}</td>
                    {TIERS.map((t) => (
                      <td key={t.key} className={`px-4 py-3 text-center ${t.highlight ? "bg-[#1E3A8A]/5" : ""}`}>
                        <Cell value={r[t.key]} highlight={t.highlight} />
                      </td>
                    ))}
                  </tr>
                ))}
                <tr className="border-t-2 border-[var(--c-border)]">
                  <td className="px-6 py-4" />
                  {TIERS.map((t) => (
                    <td key={t.key} className={`px-4 py-4 text-center ${t.highlight ? "bg-[#1E3A8A]/5" : ""}`}>
                      <button
                        data-testid={`comparison-cta-${t.key}`}
                        onClick={() => onPick(t.key)}
                        className={`rounded-full border-2 border-[var(--c-border)] px-4 py-2 text-xs font-bold shadow-[2px_2px_0px_var(--c-shadow)] transition-transform hover:-translate-y-0.5 ${
                          t.highlight ? "bg-[#1E3A8A] text-white" : "bg-[var(--c-bg)] text-[var(--c-text)]"
                        }`}
                      >
                        {t.key === "free" ? "Start free" : t.key === "premium" ? "Get the guide" : "Request service"}
                      </button>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </details>
      </div>
    </section>
  );
}
